import {
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "~/components/ui/drawer"
import { Button } from "~/components/ui/button"
import { Label } from "~/components/ui/label"
import { Separator } from "~/components/ui/separator"
import { Drawer as DrawerPrimitive } from "vaul"
import { useEffect, useState } from "react"
import { FormInput, FormSelect, SubmitButton } from "~/components/form-fields"
import { ValidatedForm } from "remix-validated-form"
import { cardValidator } from "~/routes/_app+/cards"
import { Countries } from "~/utils/data"
import { useFetcher } from "@remix-run/react"
import { action as cardAction } from "~/routes/_app+/cards"
import { Alert, AlertTitle, AlertDescription } from "~/components/ui/alert"
import { ExclamationTriangleIcon } from "@radix-ui/react-icons"

const months = Array.from({ length: 12 }, (x, i) => {
  const month = i + 1
  return month <= 9 ? "0" + month : month.toString()
})
const currentYear = new Date().getFullYear()
const years = Array.from({ length: 17 }, (_x, i) => (currentYear + i).toString())

export function CreditCardForm({ open, setOpen }: { open: boolean; setOpen: (open: boolean) => void }) {
  const [alert, setAlert] = useState("")
  const [alertDesc, setAlertDesc] = useState("")
  const fetcher = useFetcher<typeof cardAction>()

  useEffect(() => {
    if (fetcher.data?.error) {
      setAlert(fetcher.data.error.alert)
      setAlertDesc(fetcher.data.error.description)
    } else {
      setAlert("")
      setAlertDesc("")
      setOpen(false)
    }
  }, [fetcher.data])

  return (
    <DrawerPrimitive.NestedRoot open={open} onOpenChange={setOpen}>
      <DrawerContent className="z-40">
        <ValidatedForm
          className="mx-auto w-full max-w-2xl"
          validator={cardValidator}
          action="/cards"
          fetcher={fetcher}
          method="post">
          <DrawerHeader>
            <DrawerTitle>Add New Card</DrawerTitle>
            <DrawerDescription>
              <span>Enter your billing address and card details below</span>
            </DrawerDescription>
            <Separator className="my-2" />
            {alert && (
              <Alert variant="destructive">
                <ExclamationTriangleIcon className="h-4 w-4" />
                <AlertTitle>{alert}</AlertTitle>
                <AlertDescription>{alertDesc}</AlertDescription>
              </Alert>
            )}
            <div className="flex gap-4">
              <div className="flex flex-1 flex-col gap-4">
                <FormInput type="text" name="Address" label="Address" />
                <FormSelect
                  name="Country"
                  label="Country"
                  placeholder="Select Country"
                  options={Countries.map((country) => ({ value: country.code, label: country.label }))}
                />
                <FormInput type="text" name="City" label="City" />
                <div className="flex gap-4">
                  <FormInput type="text" name="State" label="State (US only)" />
                  <FormInput type="text" name="Zip" label="Zip Code" />
                </div>
              </div>
              <Separator className="my-2" orientation="vertical" />
              <div className="flex flex-1 flex-col gap-4">
                <FormInput type="text" name="CardHolder" label="Card Holder" />
                <FormInput type="text" name="CardNumber" label="Card Number" />
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label className="mx-1">Expiration Date</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <FormSelect
                        name="CardMonth"
                        placeholder="Month"
                        options={months.map((val) => ({ value: val, label: val }))}
                      />
                      <FormSelect
                        name="CardYear"
                        placeholder="Year"
                        options={years.map((val) => ({ value: val, label: val }))}
                      />
                    </div>
                  </div>
                  <FormInput type="text" name="CardCode" label="CVV" />
                </div>
              </div>
            </div>
          </DrawerHeader>
          <DrawerFooter className="grid grid-cols-2 mt-6 mb-8">
            <SubmitButton content="Add Card" loading="Adding Card..." />
            <DrawerClose asChild>
              <Button variant="outline">Cancel</Button>
            </DrawerClose>
          </DrawerFooter>
        </ValidatedForm>
      </DrawerContent>
    </DrawerPrimitive.NestedRoot>
  )
}
